import { semverCoerce, semverLt, semverValid } from './semver-min';
import { normalizeVersionString } from './strings';

/**
 * The native binary version currently installed on the device, as read from
 * `getAppVersionInfo()`. Always the NATIVE version, never the OTA/Norrix
 * runtime version; the store can only ever serve a newer binary, not a
 * newer OTA bundle.
 */
export interface InstalledNativeVersion {
  version: string;
  buildNumber?: string;
}

/**
 * The newest binary the enterprise store currently serves, as read from
 * `security.service.ts getAppInfo()` (`versionNumber`/`buildNumber`).
 */
export interface StoreLatestBinary {
  versionNumber?: string;
  buildNumber?: string | number;
}

/** What the Norrix store-prompt gate does with a `requiresStoreUpdate` signal. */
export type StorePromptDecision = 'prompt' | 'suppress' | 'wait';

/**
 * Bug 32057 cold-launch order: Norrix reports `requiresStoreUpdate` about a
 * second before the store lookup (`_checkAppInfo`) resolves. Prompting then
 * would fail open on the unknown store state and queue the toast before the
 * store's answer can suppress it. So while the store state is unknown the
 * gate WAITS, and only once the wait has expired does unknown fail open to
 * `shouldPromptStoreUpdate`'s prompt.
 */
export function decideStorePrompt(
  installedNative: InstalledNativeVersion,
  storeLatest: StoreLatestBinary | null | undefined,
  storeWaitExpired: boolean,
  lastOpenedStoreBuild?: string | null
): StorePromptDecision {
  if (!storeLatest && !storeWaitExpired) {
    return 'wait';
  }
  return shouldPromptStoreUpdate(
    installedNative,
    storeLatest,
    lastOpenedStoreBuild
  )
    ? 'prompt'
    : 'suppress';
}

/**
 * Decide whether the Norrix-driven store-update prompt should actually be
 * shown to the user, given what binary the enterprise store currently
 * serves.
 *
 * Bug 32057: Norrix decides `requiresStoreUpdate` from the native
 * fingerprint and SDK compatibility with the newest OTA, entirely
 * independent of what binary the enterprise store has actually published.
 * When Norrix runs ahead of the store (a new native release exists but the
 * store's MobileApplicationInstance row hasn't been updated to serve it
 * yet), the client opened the store, the user installed the exact binary
 * they already had, and got re-prompted forever. This function adds the
 * missing check: the store must actually serve something strictly newer
 * than what's installed before the prompt is worth showing.
 *
 * Rules:
 * - `storeLatest` is `null`/`undefined`, or its version is unparseable
 *   (store state unknown): returns `true`. This fails OPEN to the prior
 *   behavior (always prompt) rather than silently swallowing a real
 *   update because the store lookup hasn't resolved yet or errored.
 * - Store version strictly newer than installed: `true`.
 * - Same version: the device build is NEVER compared with the store build.
 *   They are on different scales: the store row's `buildNumber` is the ADO
 *   `Build.BuildId` (`devopsEnterpriseAppStoreAPI.ts`), while the device
 *   reports `APP_BUILD_NUMBER || versionCode/CFBundleVersion`, and Norrix
 *   builds set neither to the BuildId (safety Android `4` vs store `149197`;
 *   PR 4183 review 54469). But same-version native rebuilds do ship (1.4.4 at
 *   153923 and 162997), so the prompt fires ONCE per store build: `true` only
 *   while `lastOpenedStoreBuild` (the store build the user last opened the
 *   store for, persisted by the caller) differs from the store's build. After
 *   one store visit for that build it stays quiet, which is what breaks the
 *   loop. A missing store build on a tie is `false`.
 * - Store version older than installed: `false`.
 */
export function shouldPromptStoreUpdate(
  installedNative: InstalledNativeVersion,
  storeLatest: StoreLatestBinary | null | undefined,
  lastOpenedStoreBuild?: string | null
): boolean {
  if (!storeLatest || !storeLatest.versionNumber) {
    return true;
  }

  const storeVersionRaw = storeLatest.versionNumber;
  const isStoreVersionParseable =
    !!semverValid(storeVersionRaw) || !!semverCoerce(storeVersionRaw);
  if (!isStoreVersionParseable) {
    return true;
  }

  const installedVersion = normalizeVersionString(installedNative.version);
  const storeVersion = normalizeVersionString(storeVersionRaw);

  if (semverLt(installedVersion, storeVersion)) {
    return true;
  }
  if (semverLt(storeVersion, installedVersion)) {
    return false;
  }

  // Same version: prompt once per store build, never by comparing builds.
  const storeBuild = storeBuildKey(storeLatest.buildNumber);
  return storeBuild !== null && storeBuild !== (lastOpenedStoreBuild ?? null);
}

/**
 * The store build as the string the caller persists after opening the store
 * (`null` when absent or blank), so a JSON number and its digits match.
 */
export function storeBuildKey(
  buildNumber: string | number | undefined
): string | null {
  const key = buildNumber === undefined ? '' : String(buildNumber).trim();
  return key ? key : null;
}
